import { useEffect, useState } from 'react';

import { Checkbox, TextField, Select, Typography } from '@king-ict/eupisi-ui/components';
import { codeBookToSelectData, nameof } from '@king-ict/eupisi-ui/utils';
import { Col, FormLabel, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { editInstitution, getInstitutionById } from '../../api/Institution/InstitutionApi';
import { getSchoolYearList } from "../../api/SchoolYear/SchoolYearApi";
import { useAppDispatch } from "../../app/store";
import { ContentArea, FormCard, ToolbarEdit, ToolbarTop } from "../../components";
import { ValidationFailure } from "../../models";
import { ImportantDateEventEnum } from "../../models/ImportantDate/ImportantDateEventEnum";
import { InstitutionEditRequest, Institution, InstitutionSchoolYearOther, InstitutionLanguageModel, InstitutionProgram } from '../../models/Institution';
import { HierarchicalCodeBook } from "../../models/Shared/HierarchicalCodeBook";
import { useGetInstitutionShiftsQuery, useGetLanguagesQuery, useGetElectiveSubjectsQuery } from '../../queries/codeBookQuery';
import { institutionDetailsUrl } from '../../routes/routes';
import { resetIsEdited, setIsEdited } from "../../slices/applicationSlice";
import styles from "./Institutions.module.scss";

export function InstitutionEdit() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { id } = useParams<{ id: string }>();

    const createInitialInstitutionDetails = (): Institution => {
        return {
            id: 0,
            //Naziv ustanove
            name: "",
            //Šifra ustanove
            code: "",
            //Podšifra istanove
            subCode: "",
            oib: "",
            status: "",
            isSatellite: false,
            isByEnrollmentArea: false,
            isHandicapped: false,
            isPrivateInstitution: false,
            address: { houseNumber: "", street: "", town: "", municipality: "", county: "" },
            programs: [],
            //Izborni predmeti
            electiveSubjects: [],
            //Obvezni strani jezik
            mandatoryForeignLanguages: [],
            //Nastava na stranom jeziku
            foreignLanguages: [],
            //Produženi boravak
            //extendedStayClass: false,
            //Cjelodnevna nastava
            //allDayClass: false,
            //Organizacija rada u smjenama
            //shift: "",
            //Jezik nacionalne manjine
            //nationalMinorityLanguages: [],
            //Model
            languageSchoolingModels: [],
        }
    }

    const [selectedschoolYear, setSelectedSchoolYear] = useState<number | null>();
    const [institutionDetails, setInstitutionDetails] = useState<Institution>(() => createInitialInstitutionDetails());
    const [errors, setErrors] = useState<ValidationFailure[]>()

    const { data: subjects } = useGetElectiveSubjectsQuery(institutionDetails.isPrivateInstitution);
    const { data: languages } = useGetLanguagesQuery();
    const { data: institutionShifts } = useGetInstitutionShiftsQuery();

    useEffect(() => {
        const _setInstitutionDetails = async () => {
            var schoolYearList = await getSchoolYearList();
            var currentSchoolYear = schoolYearList.find(val => val.isCurrent);
            var isEditable = false;
            if (currentSchoolYear) {
                const eventId = ImportantDateEventEnum.InstitutionAdministration as number;
                var importantDate = currentSchoolYear.importantDates.find(val => val.eventId === eventId);
                if (importantDate) {
                    var exactlyNow = new Date(Date.now());
                    var dateFrom = new Date(importantDate.dateTimeFrom);
                    var dateTo = new Date(importantDate.dateTimeTo);
                    if (dateFrom <= exactlyNow && dateTo >= exactlyNow) {
                        isEditable = true;
                    }
                }
                setSelectedSchoolYear(currentSchoolYear.id);
            }
            var results = null;

            if (id && currentSchoolYear) {
                results = await getInstitutionById(+id, currentSchoolYear.id);
            }

            if (results) {
                setInstitutionDetails(results);
            }

        }
        _setInstitutionDetails();
        dispatch(resetIsEdited())
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const mapInstitutionDetailsToEditRequest = (institutionDetails: Institution): InstitutionEditRequest => {
        return {
            id: institutionDetails.id,
            programs: institutionDetails.programs,
            electiveSubjectIds: institutionDetails.electiveSubjects.map(val => val.parentId) as number[],
            mandatoryForeignLanguageIds: institutionDetails.mandatoryForeignLanguages.map(val => val.parentId) as number[],
            foreignLanguageIds: institutionDetails.foreignLanguages.map(val => val.parentId) as number[],
            extendedStayClass: institutionDetails.institutionSchoolYearOther?.extendedStayClass ?? false,
            allDayClass: institutionDetails.institutionSchoolYearOther?.allDayClass ?? false,
            languageSchoolingModels: institutionDetails.languageSchoolingModels,
            activeForEnrollment: institutionDetails.institutionSchoolYearOther?.activeForEnrollment ?? false,
            shiftId: institutionDetails.institutionSchoolYearOther?.shift?.id,
            telephoneNumber: institutionDetails.institutionSchoolYearOther?.telephoneNumber,
            email: institutionDetails.institutionSchoolYearOther?.email,
        }
    }

    const handleInstitutionProgramSwitchChange = (e: any) => {
        const entityId = e.target.name as number;

        var newPrograms = institutionDetails.programs.map(p => p.id == entityId ? { ...p, isEnrollmentModule: !p.isEnrollmentModule } : p);

        const newInstitutionDetails: Institution = {
            ...institutionDetails,
            programs: newPrograms
        };
        setInstitutionDetails(newInstitutionDetails);
        dispatch(setIsEdited());
    }
    const handleInstitutionLanguageModelSwitchChange = (e: any) => {
        const id = e.target.name as number;

        var newLanguageModels = institutionDetails.languageSchoolingModels.map(p => p.id == id ? { ...p, isEnrollmentModule: !p.isEnrollmentModule } : p);

        const newInstitutionDetails: Institution = {
            ...institutionDetails,
            languageSchoolingModels: newLanguageModels
        };
        setInstitutionDetails(newInstitutionDetails);
        dispatch(setIsEdited());
    }
    const getInstitutionProgramListItems = (list: InstitutionProgram[]) => {
        if (list && list.length > 0) {
            return (
                <div>
                    {list.map((listItem) => (
                        <Row >
                            <Col md={6}>
                                <Typography variant="medium" color="base">
                                    {listItem.name}
                                </Typography>
                            </Col>
                            <Col md={6}>
                                <Checkbox
                                    name={listItem.id.toString()}
                                    checked={listItem.isEnrollmentModule}
                                    onChange={handleInstitutionProgramSwitchChange}
                                />
                            </Col>
                        </Row>
                    ))
                    }
                </div>
            )
        }
    };
    const getInstitutionLanguageModelsListItems = (list: InstitutionLanguageModel[]) => {
        if (list && list.length > 0) {
            return (
                <div>
                    {list.map((listItem) => (
                        <Row >
                            <Col md={3}>
                                <Typography variant="medium" color="base">
                                    {listItem.languageName}
                                </Typography>
                            </Col>
                            <Col md={3}>
                                <Typography variant="medium" color="base">
                                    {listItem.modelName}
                                </Typography>
                            </Col>
                            <Col md={2}>
                                <Checkbox
                                    name={listItem.id.toString()}
                                    checked={listItem.isEnrollmentModule}
                                    onChange={handleInstitutionLanguageModelSwitchChange}
                                />
                            </Col>
                        </Row>
                    ))
                    }
                </div>
            )
        }
    };
    const handleSubjectChange = (e: any) => {
        const newSubjectIds = e.target.value as number[];
        const newInstitutionDetails: Institution = {
            ...institutionDetails,
            electiveSubjects: subjects?.filter(val => newSubjectIds.find(v => val.id === v) !== undefined).map(v => { return { id: 0, parentId: v.id, name: v.name } as HierarchicalCodeBook; }) ?? [],
        };
        setInstitutionDetails(newInstitutionDetails);
        dispatch(setIsEdited());
    }
    const handleMandatoryLanguageChange = (e: any) => {
        const newMandatoryLanguageIds = e.target.value as number[];
        const newInstitutionDetails: Institution = {
            ...institutionDetails,
            mandatoryForeignLanguages: languages?.filter(val => newMandatoryLanguageIds.find(v => v === val.id) !== undefined).map(v => { return { id: 0, parentId: v.id, name: v.name } as HierarchicalCodeBook; }) ?? [],
        };
        setInstitutionDetails(newInstitutionDetails);
        dispatch(setIsEdited());
    }
    const handleForeignLanguageChange = (e: any) => {
        const newForeignLanguageIds = e.target.value as number[];
        const newInstitutionDetails: Institution = {
            ...institutionDetails,
            foreignLanguages: languages?.filter(val => newForeignLanguageIds.find(v => v === val.id) !== undefined).map(v => { return { id: 0, parentId: v.id, name: v.name } as HierarchicalCodeBook; }) ?? [],
        };
        setInstitutionDetails(newInstitutionDetails);
        dispatch(setIsEdited());
    }
    const handleInstitutionShiftChange = (e: any) => {
        const newInstitutionShiftId = Number.parseInt(e.target.value);
        const shift = institutionShifts!.find(v => v.id === newInstitutionShiftId);
        if (shift) {
            const otherData: InstitutionSchoolYearOther = {
                ...institutionDetails!.institutionSchoolYearOther!,
                shift: shift,
            };
            const newInstitutionDetails: Institution = {
                ...institutionDetails,
                institutionSchoolYearOther: otherData,
            };
            setInstitutionDetails(newInstitutionDetails);
            dispatch(setIsEdited());
        }
    }
    const handleExtendedStayClassChanged = (e: any) => {
        const val = institutionDetails.institutionSchoolYearOther?.extendedStayClass;
        const otherData: InstitutionSchoolYearOther = {
            ...institutionDetails!.institutionSchoolYearOther!,
            extendedStayClass: !val,
        };
        const newInstitutionDetails: Institution = {
            ...institutionDetails,
            institutionSchoolYearOther: otherData,
        };
        setInstitutionDetails(newInstitutionDetails);
        dispatch(setIsEdited());
    }
    const handleAllDayClassChanged = (e: any) => {
        const val = institutionDetails.institutionSchoolYearOther?.allDayClass;
        const otherData: InstitutionSchoolYearOther = {
            ...institutionDetails!.institutionSchoolYearOther!,
            allDayClass: !val,
        };
        const newInstitutionDetails: Institution = {
            ...institutionDetails,
            institutionSchoolYearOther: otherData,
        };
        setInstitutionDetails(newInstitutionDetails);
        dispatch(setIsEdited());
    }
    const handleActiveForEnrollmentChanged = (e: any) => {
        const val = institutionDetails.institutionSchoolYearOther?.activeForEnrollment;
        const otherData: InstitutionSchoolYearOther = {
            ...institutionDetails!.institutionSchoolYearOther!,
            activeForEnrollment: !val,
        };
        const newInstitutionDetails: Institution = {
            ...institutionDetails,
            institutionSchoolYearOther: otherData,
        };
        setInstitutionDetails(newInstitutionDetails);
        dispatch(setIsEdited());
    }
    const handleTelephoneNumberChanged = (e: any) => {
        const val = e.target.value as string;
        const otherData: InstitutionSchoolYearOther = {
            ...institutionDetails!.institutionSchoolYearOther!,
            telephoneNumber: val && val.length > 0 ? val : undefined,
        };
        const newInstitutionDetails: Institution = {
            ...institutionDetails,
            institutionSchoolYearOther: otherData,
        };
        setInstitutionDetails(newInstitutionDetails);
        dispatch(setIsEdited());
    }
    const handleEmailChanged = (e: any) => {
        const val = e.target.value as string;
        const otherData: InstitutionSchoolYearOther = {
            ...institutionDetails!.institutionSchoolYearOther!,
            email: val && val.length > 0 ? val : undefined,
        };
        const newInstitutionDetails: Institution = {
            ...institutionDetails,
            institutionSchoolYearOther: otherData,
        };
        setInstitutionDetails(newInstitutionDetails);
        dispatch(setIsEdited());
    }

    return (
        <ContentArea>
            <ToolbarTop
                showBack={"show"}
                pageTitle={t("common.institutionDetails.institutionDetailsInfo")}
            />
            <FormCard
                cardTitle={t("common.institutionDetails.institutiBasicInfo")}
            >
                <Row>
                    <Col md={4}>
                        <TextField
                            label={t("common.institutionDetails.name")}
                            value={institutionDetails.name}
                            readonly
                            plaintext
                        />
                    </Col>
                    <Col md={4}>
                        <TextField
                            label={t("common.institutionDetails.code")}
                            value={institutionDetails.code}
                            readonly
                            plaintext
                        />
                    </Col>
                    <Col md={4}>
                        <TextField
                            label={t("common.institutionDetails.schoolYear")}
                            value={institutionDetails.schoolYear?.name}
                            readonly
                            plaintext
                        />
                    </Col>
                </Row>
            </FormCard>
            <FormCard
                cardTitle={t("common.institutionDetails.institutionDetailsPrograms")}
            >
                <Row>
                    <Col md={6}>
                        <Typography variant="small" component="h3">
                            {t("common.institutionEdit.programsName")}
                        </Typography>
                    </Col>
                    <Col md={6} >
                        <Typography variant="small" component="h3">
                            {t("common.institutionEdit.programIsActiveName")}
                        </Typography>
                    </Col>
                </Row>
                <Row>
                    <Col md={12}>
                        {getInstitutionProgramListItems(institutionDetails.programs)}
                    </Col>
                </Row>
            </FormCard>
            <FormCard
                cardTitle={t("common.institutionDetails.institutionDetailsOther")}
            >
                <Row>
                    <Col md={4}>
                        <Select
                            multiple
                            label={t("common.institutionDetails.izborniPredmeti")}
                            name={nameof<typeof institutionDetails>("electiveSubjects")}
                            value={institutionDetails.electiveSubjects.map(val => val.parentId)}
                            onChange={handleSubjectChange}
                            selectData={codeBookToSelectData(subjects)}
                            errorText={errors?.find(e => e.propertyName.startsWith("ElectiveSubject"))?.message}
                        />
                    </Col>
                    <Col md={4}>
                        <Select
                            multiple
                            label={t("common.institutionDetails.obavezniStraniJezik")}
                            name={nameof<typeof institutionDetails>("mandatoryForeignLanguages")}
                            value={institutionDetails.mandatoryForeignLanguages.map(val => val.parentId)}
                            onChange={handleMandatoryLanguageChange}
                            selectData={codeBookToSelectData(languages)}
                        />
                    </Col>
                    <Col md={4}>
                        <Select
                            multiple
                            label={t("common.institutionDetails.straniJezici")}
                            name={nameof<typeof institutionDetails>("foreignLanguages")}
                            value={institutionDetails.foreignLanguages.map(val => val.parentId)}
                            onChange={handleForeignLanguageChange}
                            selectData={codeBookToSelectData(languages)}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col md={4}>
                        <Checkbox
                            label={t("common.institutionEdit.extendedStayClass")}
                            name={"institutionDetails.institutionSchoolYearOther.extendedStayClass"}
                            checked={institutionDetails.institutionSchoolYearOther?.extendedStayClass}
                            onChange={handleExtendedStayClassChanged}
                        />
                    </Col>
                    <Col md={4}>
                        <Checkbox
                            label={t("common.institutionEdit.allDayClass")}
                            name={"institutionDetails.institutionSchoolYearOther.allDayClass"}
                            checked={institutionDetails.institutionSchoolYearOther?.allDayClass}
                            onChange={handleAllDayClassChanged}
                        />
                    </Col>
                    <Col md={4}>
                        <Checkbox
                            label={t("common.institutionEdit.activeForEnrollment")}
                            name={"institutionDetails.institutionSchoolYearOther.activeForEnrollment"}
                            checked={institutionDetails.institutionSchoolYearOther?.activeForEnrollment}
                            onChange={handleActiveForEnrollmentChanged}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col md={8}>
                        {getInstitutionLanguageModelsListItems(institutionDetails.languageSchoolingModels)}
                    </Col>
                    <Col md={4}>
                        <Select
                            label={t("common.institutionEdit.shifts")}
                            name={"institutionDetails.institutionSchoolYearOther.shift.id"}
                            value={institutionDetails.institutionSchoolYearOther?.shift?.id}
                            onChange={handleInstitutionShiftChange}
                            selectData={codeBookToSelectData(institutionShifts)}
                        />
                    </Col>
                </Row>
            </FormCard>
            <FormCard
                cardTitle={t("common.institutionDetails.institutionContactDetails")}
            >
                <Row>
                    <Col md={4}>
                        <TextField
                            label={t("common.phone")}
                            value={institutionDetails.institutionSchoolYearOther?.telephoneNumber}
                            onChange={handleTelephoneNumberChanged}
                            errorText={errors?.find(e => e.propertyName === "TelephoneNumber")?.message}
                        />
                    </Col>
                    <Col md={4}>
                        <TextField
                            label={t("common.email")}
                            value={institutionDetails.institutionSchoolYearOther?.email}
                            onChange={handleEmailChanged}
                            errorText={errors?.find(e => e.propertyName === "Email")?.message}
                        />
                    </Col>
                </Row>
            </FormCard>
            <ToolbarEdit
                showCancel={"show"}
                showSave={"show"}
                redirectUrlSaveOK={institutionDetailsUrl}
                handleSave={async () => editInstitution(mapInstitutionDetailsToEditRequest(institutionDetails))}
                setSaveErrors={setErrors}
            />
        </ContentArea>
    )

}